// main.js — entry: renderer + scene + camera, animation loop, and the
// interaction layer (selection prompt, info panel, cart panel, toast).
import * as THREE from 'three';
import { buildScene, updateAnimations } from './scene.js';
import { createControls } from './controls.js';
import { createTouchControls } from './touch-controls.js';
import { loadProducts, buildProducts, updateSelected, mergeInventory, applyAvailabilityVisuals } from './products.js';
import { startInventoryPolling } from './inventory.js';
import * as cart from './cart.js';

// ---------- Renderer ----------
// `precision: 'mediump'` is critical on mobile. Three.js issues #14137 and
// #21805 document that Three.js falsely reports `highp` support on a wide
// range of Android GPUs (including ARM Mali) that don't actually implement
// it reliably — resulting in textures rendering as solid black on those
// devices. Forcing mediump applies on every shader the renderer compiles
// (Lambert, Basic, sprite, etc.) and matches what mobile games and
// production WebGL apps do for consistent cross-device rendering.
//
// Detect touch / mobile up-front so the renderer is constructed with the
// right precision; switching after construction doesn't recompile shaders.
const canvas = document.getElementById('scene');
const _isMobile = matchMedia('(hover: none) and (pointer: coarse)').matches
               || ('ontouchstart' in window)
               || new URLSearchParams(location.search).has('touch');
const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  precision: _isMobile ? 'mediump' : 'highp',
  // Logarithmic depth buffer — gives roughly constant depth precision at
  // every distance from the camera instead of the linear falloff that's
  // standard with a perspective camera. Critical on mobile because the
  // mediump 16-bit-effective depth buffer otherwise loses precision fast
  // past 5-10m, producing the "wall-attached items flicker only when far
  // from the player" symptom. Industry-standard fix for this exact issue
  // (see Three.js docs and forum threads on z-fighting at distance).
  logarithmicDepthBuffer: true,
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
// updateStyle=false: leave the CSS-driven display box (100vw/100dvh) alone so
// it stays responsive across rotations. Must match the resize handler below —
// if this wrote inline px styles, clientWidth would freeze at the boot size.
renderer.setSize(window.innerWidth, window.innerHeight, false);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.shadowMap.enabled = false;

// ---------- Scene + Camera ----------
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x140820);

// Far plane tightened from 100 to 40. The room's longest visible diagonal
// is ~24m, so 40m covers everything (including the "outside backdrop" seen
// through the doors) while giving the depth buffer 2.5× more usable range
// than a 100m far plane. Combined with logarithmicDepthBuffer above this
// gives uniform precision at every distance — no more distance-based
// z-fighting.
const camera = new THREE.PerspectiveCamera(72, window.innerWidth / window.innerHeight, 0.1, 40);
camera.position.set(0, 1.65, 6);

buildScene(scene);

// ---------- Controls ----------
// Pick the input layer at boot. Phones/tablets get the touch module (floating
// joystick + drag-to-look + tap-to-interact); desktops keep PointerLockControls
// + WASD. Both expose the same API ({ object, update, isLocked, lockPointer,
// unlockPointer }) so everything below this is input-agnostic.
//
// `?touch=1` forces the touch path on a desktop browser — handy for testing
// mobile UI without a real phone (Chrome devtools' device emulation doesn't
// always set pointer: coarse).
const isTouch = _isMobile;
document.body.classList.toggle('touch-mode', isTouch);

const controls = isTouch
  ? createTouchControls(camera, renderer.domElement)
  : createControls(camera, renderer.domElement);
scene.add(controls.object);

// (Removed camera-attached lantern — it was a possible source of weird
// shading on Mali-G715. Products now use MeshBasicMaterial for the front
// face so they're guaranteed visible regardless of scene lighting; the
// 4 corner PointLights + Ambient(1.5) + Hemisphere(1.2) carry the room.)

// ---------- DOM refs ----------
const crosshair  = document.getElementById('crosshair');
const prompt     = document.getElementById('prompt');
const promptName = document.getElementById('prompt-name');
const promptPrice= document.getElementById('prompt-price');

const infoOverlay = document.getElementById('info-overlay');
const infoImage   = document.getElementById('info-image');
const infoName    = document.getElementById('info-name');
const infoMeta    = document.getElementById('info-meta');
const infoDesc    = document.getElementById('info-desc');
const infoDescToggle = document.getElementById('info-desc-toggle');
const infoPrice   = document.getElementById('info-price');
const infoLink    = document.getElementById('info-link');
const infoAdd     = document.getElementById('info-add');
const infoClose   = document.getElementById('info-close');

const cartCounter = document.getElementById('cart-counter');
const cartOverlay = document.getElementById('cart-overlay');
const cartClose   = document.getElementById('cart-close');
const cartCheckout= document.getElementById('cart-checkout');
const cartClear   = document.getElementById('cart-clear');

const toast = document.getElementById('toast');

// ---------- Resize ----------
// Recompute from the canvas's *displayed* size (CSS 100vw/100dvh), not
// window.inner*, which on mobile fires against stale/mismatched dimensions on
// orientation change and reads as a zoomed-out, "pushed back" projection.
// setSize's updateStyle=false leaves the CSS display box alone (the boot
// setSize above does the same) so clientWidth/clientHeight track rotation.
function applyViewport() {
  const w = canvas.clientWidth  || window.innerWidth;
  const h = canvas.clientHeight || window.innerHeight;
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h, false);
}
window.addEventListener('resize', applyViewport);
// orientationchange often fires before viewport metrics settle — defer one
// frame so we read post-rotation dimensions.
window.addEventListener('orientationchange', () => requestAnimationFrame(applyViewport));
// visualViewport resize catches the iOS address-bar show/hide animation.
if (window.visualViewport) window.visualViewport.addEventListener('resize', applyViewport);
applyViewport();

// ---------- Cart wiring ----------
cart.loadFromStorage();
cart.subscribe(() => {
  cart.renderCart();
  cartCounter.dataset.empty = cart.getCount() === 0 ? 'true' : 'false';
});
cart.renderCart();
cartCounter.dataset.empty = cart.getCount() === 0 ? 'true' : 'false';

// ---------- Toast ----------
let toastTimer = null;
function showToast(text) {
  toast.textContent = text;
  toast.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('show'), 1500);
}

// ---------- Panel state ----------
// `panelOpen` is true while either the info modal or cart drawer is showing.
// We use it to (a) suppress in-game key handling, (b) prevent click-anywhere
// from re-locking pointer when the user is interacting with panel UI, and
// (c) tell the pointer-lock-change listener NOT to bring back the start
// overlay (which has higher z-index and would cover the panel).
let panelOpen = false;
let infoPanelProduct = null; // currently displayed product in info panel

const overlayStart = document.getElementById('overlay-start');

// Single source of truth for the start overlay: hide it whenever a panel is
// open OR pointer is locked; show it only when neither is true (genuine pause).
function refreshStartOverlay() {
  if (panelOpen || controls.isLocked()) overlayStart.hidden = true;
  else overlayStart.hidden = false;
}

// React to native pointer-lock changes (covers both controls.lock() calls and
// the user pressing Esc to unlock).
document.addEventListener('pointerlockchange', refreshStartOverlay);
// If a pointer-lock attempt fails (browser cooldown after Esc, mostly), make
// sure the start overlay surfaces so the user can click to resume.
document.addEventListener('pointerlockerror', refreshStartOverlay);

// Close a panel and try to slide straight back into walking mode. Must be
// called from a real user-gesture handler (button click / backdrop click) so
// the browser allows the re-lock; if the lock attempt fails (e.g. cooldown
// from Esc) the pointerlockerror handler above brings the overlay back.
function closeAndResume(closeFn) {
  closeFn();
  if (panelOpen) return;             // another panel is still open, do nothing
  // Optimistically hide the start overlay so we never see it flash for one
  // frame between "panel closed" and "lock acquired".
  overlayStart.hidden = true;
  try { controls.lockPointer(); }
  catch (e) { refreshStartOverlay(); }
}

function isInfoOpen() { return !infoOverlay.hidden; }
function isCartOpen() { return !cartOverlay.hidden; }

function openInfoPanel(product) {
  if (!product) return;
  infoPanelProduct = product;
  infoImage.src = product.imageUrl;
  infoImage.alt = product.name;
  infoName.textContent = product.name;
  infoDesc.textContent = product.description || '';
  infoPrice.textContent = '$' + Number(product.price).toFixed(2);
  infoLink.href = product.url;

  infoMeta.innerHTML = '';
  if (product.subtitle) infoMeta.appendChild(chip(product.subtitle, 'primary'));
  if (product.category) infoMeta.appendChild(chip(product.category));
  if (product.thc != null && product.thc > 0) infoMeta.appendChild(chip(product.thc + 'mg THC / piece'));
  else if (product.tier === 'unknown') infoMeta.appendChild(chip('THC info on label'));
  if (product.tier === 'wellness') infoMeta.appendChild(chip('CBD/CBN — 0 THC'));
  if (product.available === false) infoMeta.appendChild(chip('Sold out', 'soldout'));

  // Disable the Add to Cart button when Shopify says this variant is
  // unavailable. Re-enabled the next time the panel opens for an in-stock
  // product (or — once we land it — when the 5-min poll flips it back).
  const soldOut = product.available === false;
  infoAdd.disabled = soldOut;
  infoAdd.textContent = soldOut ? 'Sold Out' : 'Add to Cart';

  // Description: collapse to 4 lines by default; show See more if there's
  // more than that. We measure after the layout settles by comparing
  // scrollHeight (full) to clientHeight (clamped).
  infoDesc.classList.remove('expanded');
  // Defer to next frame so the browser has applied the line-clamp before we
  // measure. If the description fits in 4 lines, hide the toggle.
  requestAnimationFrame(() => {
    const overflows = infoDesc.scrollHeight > infoDesc.clientHeight + 1;
    infoDescToggle.hidden = !overflows;
    infoDescToggle.textContent = 'See more';
  });

  infoOverlay.hidden = false;
  panelOpen = true;
  overlayStart.hidden = true;     // belt-and-suspenders — stop the start overlay flashing
  if (controls.isLocked()) controls.unlockPointer();
}
function closeInfoPanel() {
  infoOverlay.hidden = true;
  infoPanelProduct = null;
  panelOpen = isCartOpen();
  refreshStartOverlay();
}

function openCart() {
  cartOverlay.hidden = false;
  panelOpen = true;
  overlayStart.hidden = true;
  if (controls.isLocked()) controls.unlockPointer();
}
function closeCart() {
  cartOverlay.hidden = true;
  panelOpen = isInfoOpen();
  refreshStartOverlay();
}

// Toggles route through closeAndResume on the close path so pressing C/E
// to dismiss a panel takes you straight back into walking mode (re-locks
// pointer in the user-gesture context of the keypress) instead of bouncing
// through the start overlay.
function toggleCart() {
  if (isCartOpen()) closeAndResume(closeCart);
  else openCart();
}
function toggleInfoForCurrent() {
  if (isInfoOpen()) closeAndResume(closeInfoPanel);
  else if (currentSelection) openInfoPanel(currentSelection);
}

function chip(text, variant) {
  const el = document.createElement('span');
  el.className = variant ? `chip chip-${variant}` : 'chip';
  el.textContent = text;
  return el;
}

// ---------- Info / cart button handlers ----------
// X and backdrop handlers go through closeAndResume so we go straight back
// into walking mode without bouncing through the start overlay. (The Esc
// keypath uses plain closeInfoPanel/closeCart — browser blocks immediate
// re-lock after Esc, so the overlay-as-resume-hint is the right fallback.)
infoClose.addEventListener('click', (e) => { e.stopPropagation(); closeAndResume(closeInfoPanel); });
infoDescToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  const expanded = infoDesc.classList.toggle('expanded');
  infoDescToggle.textContent = expanded ? 'See less' : 'See more';
});
infoAdd.addEventListener('click', (e) => {
  e.stopPropagation();
  if (!infoPanelProduct) return;
  if (cart.addItem(infoPanelProduct)) {
    showToast(`Added "${infoPanelProduct.name}"`);
  } else {
    showToast(`"${infoPanelProduct.name}" is sold out`);
  }
});
infoOverlay.addEventListener('click', (e) => {
  if (e.target === infoOverlay) closeAndResume(closeInfoPanel);
});

cartCounter.addEventListener('click', (e) => { e.stopPropagation(); openCart(); });
cartClose.addEventListener('click', (e) => { e.stopPropagation(); closeAndResume(closeCart); });
cartOverlay.addEventListener('click', (e) => {
  if (e.target === cartOverlay) closeAndResume(closeCart);
});
cartCheckout.addEventListener('click', (e) => {
  e.stopPropagation();
  if (cart.getCount() === 0) return;

  // Filter out anything we can't safely hand off (missing variantId, or item
  // went OOS since the user added it).
  const lineItems = cart.getItems()
    .filter(({ product }) => product.variantId && product.available !== false);

  if (lineItems.length === 0) {
    showToast('Cart unavailable — try refreshing');
    return;
  }

  // Hand off via form POST to /cart/add, NOT via the /cart/<id>:<qty>
  // permalink. The permalink format gets auto-forwarded to checkout on stores
  // that have "require login at checkout" or any direct-checkout setting,
  // which then bounces the customer to a login page. POSTing to /cart/add
  // with an explicit `return_to=/cart` forces Shopify to land them on the
  // cart page with all items pre-loaded — they can then click Checkout
  // themselves when they're ready (and log in then if needed).
  //
  // Form POST with target="_blank" opens in a new tab and is NOT popup-
  // blocked the way window.open() is — browsers treat it as a navigation
  // initiated by a real user gesture.
  showToast('Opening your retrobakery.net cart…');

  const form = document.createElement('form');
  form.action = 'https://retrobakery.net/cart/add';
  form.method = 'POST';
  form.target = '_blank';
  form.acceptCharset = 'UTF-8';
  form.style.display = 'none';

  lineItems.forEach(({ product, qty }, i) => {
    appendHidden(form, `items[${i}][id]`, String(product.variantId));
    appendHidden(form, `items[${i}][quantity]`, String(qty));
  });
  appendHidden(form, 'return_to', '/cart');

  document.body.appendChild(form);
  form.submit();
  form.remove();
});

function appendHidden(form, name, value) {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  form.appendChild(input);
}
cartClear.addEventListener('click', (e) => {
  e.stopPropagation();
  if (cart.getCount() === 0) return;
  if (confirm('Clear all items from your cart?')) cart.clear();
});

// ---------- Touch-only wiring ----------
// On touch there's no pointer lock to acquire, so the desktop click-to-lock
// flow below doesn't hide the start overlay. Tapping anywhere on the overlay
// dismisses it instead.
if (isTouch) {
  overlayStart.addEventListener('click', (e) => {
    if (e.target.closest('a')) return;        // let the "back to retrobakery.net" link work
    overlayStart.hidden = true;
  });
  // Tap → open the info panel for whatever the finger landed on. touch-controls
  // forwards the tap's client coords; we raycast there instead of the fixed
  // screen centre (which always resolved to the middle shelf in portrait) and
  // commit the hit to currentSelection before opening the panel.
  const _tapRay = new THREE.Raycaster();
  const _tapNdc = new THREE.Vector2();
  function pickAtClient(clientX, clientY) {
    // NDC from the canvas's displayed rect (not window.inner*) keeps the tap
    // ray aligned with what's on screen while mobile browser chrome animates.
    const rect = canvas.getBoundingClientRect();
    _tapNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    _tapNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    _tapRay.setFromCamera(_tapNdc, camera);
    _tapRay.far = 40;                 // camera far plane — reach wall cases across the room
    const meshes = productEntries.map(e => e.mesh);
    const hits = _tapRay.intersectObjects(meshes, false);
    if (!hits.length) return null;
    const entry = productEntries.find(e => e.mesh === hits[0].object);
    return entry ? entry.data : null;
  }
  // The browser synthesizes a click after every tap, hit-tested at dispatch
  // time. Once the panel opens, that click would land on whatever now sits
  // under the finger (Add to Cart, the View link, or the backdrop) and press
  // it. Swallow only that one synthesized click: same spot, right after the
  // tap. Anything else passes through untouched.
  function swallowPostTapClick(x, y) {
    const swallow = (e) => {
      if (Math.hypot(e.clientX - x, e.clientY - y) > 24) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      document.removeEventListener('click', swallow, true);
    };
    document.addEventListener('click', swallow, true);
    setTimeout(() => document.removeEventListener('click', swallow, true), 700);
  }
  controls.onTap((tap) => {
    if (panelOpen) return;
    const sel = tap ? pickAtClient(tap.x, tap.y) : currentSelection;
    if (sel) {
      currentSelection = sel;
      if (tap) swallowPostTapClick(tap.x, tap.y);
      openInfoPanel(sel);
    }
  });
}

// Portrait-orientation hint: dismissible. Once dismissed it stays dismissed
// for the rest of the session even if the user rotates back to portrait.
const portraitContinueBtn = document.getElementById('portrait-continue');
if (portraitContinueBtn) {
  portraitContinueBtn.addEventListener('click', () => {
    document.body.classList.add('portrait-dismissed');
  });
}

// ---------- Pointer-lock click handling ----------
// Only re-lock when the click target is the canvas or the start overlay.
// Clicking a panel/button must NOT relock — buttons need a chance to fire.
document.addEventListener('click', (e) => {
  if (!e.isTrusted) return;
  if (controls.isLocked()) return;
  if (panelOpen) return;
  // Skip anchor clicks inside the overlay so the "back to retrobakery.net"
  // link can navigate without us trying to grab pointer lock first.
  if (e.target.closest('a')) return;
  if (e.target === canvas || e.target.closest('#overlay-start')) {
    controls.lockPointer();
  }
});
// Suppress browser context menu — we use RMB as the "add to cart" action.
window.addEventListener('contextmenu', (e) => e.preventDefault());

// ---------- Selection / interaction wiring ----------
let currentSelection = null;        // product data currently looked-at
let productEntries = [];

function refreshPrompt() {
  if (!currentSelection || panelOpen || !controls.isLocked()) {
    prompt.hidden = true;
    crosshair.classList.remove('active');
    return;
  }
  prompt.hidden = false;
  crosshair.classList.add('active');
  // Show "Name — subtitle" so the cannabinoid dose is right there with the
  // name when you look at a product. Falls back gracefully if no subtitle.
  promptName.textContent = currentSelection.subtitle
    ? `${currentSelection.name} · ${currentSelection.subtitle}`
    : currentSelection.name;
  promptPrice.textContent = '$' + Number(currentSelection.price).toFixed(2);
}

function primaryAction() {        // E / LMB → info
  if (panelOpen) return;
  if (!currentSelection) return;
  openInfoPanel(currentSelection);
}
function secondaryAction() {      // F / RMB → add to cart
  if (panelOpen) return;
  if (!currentSelection) return;
  if (cart.addItem(currentSelection)) {
    showToast(`+1 ${currentSelection.name}`);
  } else {
    showToast(`Sold out — ${currentSelection.name}`);
  }
}

// Keyboard
window.addEventListener('keydown', (e) => {
  // Esc → close any open panel. Pointer also unlocks via the browser default.
  if (e.code === 'Escape') {
    if (!infoOverlay.hidden) { closeInfoPanel(); return; }
    if (!cartOverlay.hidden) { closeCart(); return; }
  }
  // C is a toggle and works regardless of game state, EXCEPT when the info
  // modal is open (avoid stacking dialogs).
  if (e.code === 'KeyC' && !isInfoOpen()) {
    toggleCart();
    return;
  }
  // E toggles the info panel for the currently looked-at product. Open or
  // close based on current state.
  if (e.code === 'KeyE' && !isCartOpen()) {
    toggleInfoForCurrent();
    return;
  }
  // Skip the rest while a panel is open or while the user is typing.
  if (panelOpen) return;
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
  if (e.code === 'KeyF') secondaryAction();
});

// Mouse buttons (only when pointer is locked, so we don't steal clicks from UI)
window.addEventListener('mousedown', (e) => {
  if (!controls.isLocked()) return;
  if (panelOpen) return;
  if (e.button === 0) {              // LMB
    e.preventDefault();
    primaryAction();
  } else if (e.button === 2) {       // RMB
    e.preventDefault();
    secondaryAction();
  }
});

// ---------- Bootstrap product load + animation loop ----------
(async function init() {
  let products = [];
  try {
    products = await loadProducts('/products.json');
    productEntries = buildProducts(scene, products);
    // Backfill any pre-existing localStorage cart entries with the live
    // variantId/available/price now that products are merged.
    cart.reconcileWithProducts(products);
  } catch (err) {
    console.error('[Retro Bakery] failed to load products:', err);
  }

  // Refresh inventory every 5 minutes so customers see current stock without
  // a page reload. Also fires once when the tab regains focus after being
  // hidden — see startInventoryPolling.
  if (products.length > 0) {
    startInventoryPolling((inv) => {
      const flipped = mergeInventory(products, inv);
      if (flipped.size > 0) {
        for (const entry of productEntries) {
          if (flipped.has(entry.data.id)) applyAvailabilityVisuals(entry);
        }
      }
      cart.reconcileWithProducts(products);
    });
  }

  // Debug hooks (handy in DevTools / Claude Preview)
  window.__retro = { THREE, scene, camera, renderer, controls, cart, get products() { return productEntries; } };

})();

const clock = new THREE.Clock();
function animate() {
  const dt = Math.min(clock.getDelta(), 0.1);
  controls.update(dt);

  // Per-frame scene animation (lava-lamp blobs etc.)
  updateAnimations(clock.elapsedTime);

  // Selection: raycaster from camera through screen center
  const newSel = updateSelected(camera, productEntries, 4.0);
  if (newSel?.id !== currentSelection?.id) {
    currentSelection = newSel;
    refreshPrompt();
  }

  renderer.render(scene, camera);
  requestAnimationFrame(animate);
}
animate();
